import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import './ComboBox.css';

interface ComboBoxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  label?: string;
  allowCustom?: boolean;
  id?: string;
}

const ComboBox: React.FC<ComboBoxProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select or type...",
  label,
  allowCustom = true,
  id
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (inputValue) {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [inputValue, options]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
    setIsOpen(true);
  };

  const handleOptionSelect = (option: string) => {
    setInputValue(option);
    onChange(option);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length > 0 && !filteredOptions.includes(inputValue)) {
        // If there are filtered options but current input doesn't match exactly, select first option
        const firstOption = filteredOptions[0];
        handleOptionSelect(firstOption);
      } else {
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const showCustomOption = allowCustom && inputValue && !options.includes(inputValue) && inputValue.trim() !== '';

  return (
    <div className="combobox-container" ref={dropdownRef}>
      {label && <label htmlFor={id} className="combobox-label">{label}</label>}
      <div className="combobox-input-container">
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="combobox-input"
          autoComplete="off"
        />
        <button
          type="button"
          className="combobox-toggle"
          onClick={() => setIsOpen(!isOpen)}
        >
          <ChevronDown size={16} className={`chevron ${isOpen ? 'open' : ''}`} />
        </button>
      </div>

      {isOpen && (
        <div className="combobox-dropdown">
          {showCustomOption && (
            <div
              className="combobox-option custom-option"
              onClick={() => handleOptionSelect(inputValue)}
            >
              <Plus size={14} />
              <span>Add "{inputValue}"</span>
            </div>
          )}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={index}
                className="combobox-option"
                onClick={() => handleOptionSelect(option)}
              >
                {option}
              </div>
            ))
          ) : !showCustomOption ? (
            <div className="combobox-no-options">No options found</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default ComboBox; 